import Axios from "axios";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { Store } from "../../Store";
import { getError } from "../../utils";
import { api } from "../../Api";

const DEFAULT_LOGO =
  "https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png";

const STATUS_BADGE = {
  pending: "warning",
  processing: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "secondary",
};

const tk = (n) => `Tk ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

const EMPTY = { q: "", status: "", merchant: "", from: "", to: "" };

/** Every Request Money (member asks an upline merchant for Shopping Balance). Read-only. */
export default function MoneyRequestsScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = { headers: { authorization: `Bearer ${userInfo.token}` } };

  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState({ rows: [], page: 1, pages: 1, count: 0, summary: {} });
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (f = filters, page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize: 20 });
        Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
        const { data } = await Axios.get(api + `/api/money-requests/admin?${params.toString()}`, auth);
        setRows(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    load(EMPTY, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => {
    setFilters(form);
    load(form, 1);
  };
  const set = (k) => (e) => setForm((cur) => ({ ...cur, [k]: e.target.value }));
  const summary = rows.summary || {};

  return (
    <div className="mr-admin-screen">
      <Helmet>
        <title>Money Requests</title>
      </Helmet>
      <style>{CSS}</style>

      <div className="mr-admin-hero">
        <h3 className="mb-1">
          <i className="fa fa-hand-o-up me-2" aria-hidden="true" /> Money Requests
        </h3>
        <p className="mb-0 small">
          Members asking a merchant above them for Shopping Balance after paying them outside the app. Read-only.
        </p>
      </div>

      <div className="mr-admin-stats">
        <div><b>{summary.pending || 0}</b><span>Pending · {tk(summary.pendingAmount)}</span></div>
        <div><b>{summary.approved || 0}</b><span>Approved · {tk(summary.approvedAmount)}</span></div>
        <div><b>{summary.rejected || 0}</b><span>Rejected</span></div>
        <div><b>{summary.cancelled || 0}</b><span>Cancelled</span></div>
      </div>

      <Card className="mr-admin-card">
        <Card.Body>
          <div className="mr-admin-filters">
            <Form.Control size="sm" placeholder="Member, merchant, Trx ID or MR-…" value={form.q} onChange={set("q")} onKeyDown={(e) => e.key === "Enter" && apply()} />
            <Form.Control size="sm" placeholder="Merchant username" value={form.merchant} onChange={set("merchant")} onKeyDown={(e) => e.key === "Enter" && apply()} />
            <Form.Select size="sm" value={form.status} onChange={set("status")}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
            <Form.Control size="sm" type="date" value={form.from} onChange={set("from")} title="From date" />
            <Form.Control size="sm" type="date" value={form.to} onChange={set("to")} title="To date" />
            <Button size="sm" onClick={apply}>Filter</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => { setForm(EMPTY); setFilters(EMPTY); load(EMPTY, 1); }}>
              Clear
            </Button>
          </div>

          <Card.Title className="h6 my-3">{rows.count} request(s)</Card.Title>

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : rows.rows.length === 0 ? (
            <div className="text-muted small py-3">No money requests match.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Member</th>
                    <th>Merchant</th>
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th>Trx ID / note</th>
                    <th>Requested</th>
                    <th>Done</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.rows.map((r) => (
                    <tr key={r._id}>
                      <td className="small fw-semibold">{r.serialText}</td>
                      <td>
                        <div className="fw-semibold">{r.requesterName || r.requesterUsername}</div>
                        <div className="small text-muted">@{r.requesterUsername}</div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img src={r.associationLogo || DEFAULT_LOGO} alt="" className="mr-admin-avatar" />
                          <div>
                            <div className="fw-semibold">{r.associationName || r.merchantName}</div>
                            <div className="small text-muted">@{r.merchantUsername}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-end fw-semibold">{tk(r.amountBdt)}</td>
                      <td>
                        <Badge bg={STATUS_BADGE[r.status] || "secondary"}>{r.status}</Badge>
                        {r.otpUsed ? <div className="small text-muted">OTP verified</div> : null}
                        {r.reason ? <div className="small text-danger">{r.reason}</div> : null}
                      </td>
                      <td className="small text-muted">
                        {r.trxId || "—"}
                        {r.note ? <div>{r.note}</div> : null}
                      </td>
                      <td className="small text-muted">{fmtDate(r.createdAt)}</td>
                      <td className="small text-muted">
                        {fmtDate(r.approvedAt || r.rejectedAt || r.cancelledAt)}
                        {r.status === "approved" ? (
                          <div>
                            After: member {tk(r.requesterBalanceAfter)} · merchant {tk(r.merchantBalanceAfter)}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {r.status === "approved" ? (
                          <a
                            className="small"
                            target="_blank"
                            rel="noreferrer"
                            href={`${api}/api/money-requests/${r._id}/receipt.pdf?token=${userInfo.token}&download=1`}
                          >
                            PDF
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button size="sm" variant="outline-secondary" disabled={rows.page <= 1} onClick={() => load(filters, rows.page - 1)}>
                Prev
              </Button>
              <span className="small align-self-center">Page {rows.page} / {rows.pages}</span>
              <Button size="sm" variant="outline-secondary" disabled={rows.page >= rows.pages} onClick={() => load(filters, rows.page + 1)}>
                Next
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

const CSS = `
.mr-admin-screen { padding: 16px; max-width: 1300px; margin: 0 auto; }
.mr-admin-hero {
  background: linear-gradient(135deg, #2f8fc4 0%, #42b4e7 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 12px;
}
.mr-admin-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
.mr-admin-stats > div { background: #fff; border-radius: 12px; padding: 10px 12px; display: flex;
  flex-direction: column; box-shadow: 0 2px 10px rgba(0,0,0,.06); }
.mr-admin-stats b { font-size: 20px; color: #14506e; }
.mr-admin-stats span { font-size: 12px; color: #6b7280; }
.mr-admin-filters { display: flex; flex-wrap: wrap; gap: 8px; }
.mr-admin-filters > * { flex: 1 1 150px; max-width: 240px; }
.mr-admin-filters > .btn { flex: 0 0 auto; }
.mr-admin-card { border-radius: 14px; border: none; box-shadow: 0 2px 10px rgba(0,0,0,.06); }
.mr-admin-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #f1f3f5; }
@media (max-width: 576px) {
  .mr-admin-screen { padding: 10px; }
  .mr-admin-hero { padding: 16px; }
  .mr-admin-stats { grid-template-columns: repeat(2, 1fr); }
  .mr-admin-filters > * { max-width: none; }
}
`;
